import React, { useEffect, useState } from 'react';
import { getCookie, setCookie } from '@/utils/cookies';
import { TextField } from '@mui/material';

type PasswordGateProps = {
  isProtected: boolean;
  children: React.ReactNode;
};

const COOKIE_NAME = 'protected_page_access';

const PasswordGate = ({ isProtected, children }: PasswordGateProps) => {
  const [hasAccess, setHasAccess] = useState(!isProtected);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isProtected) {
      return;
    }
    const cookieValue = getCookie(COOKIE_NAME);
    setHasAccess(cookieValue === 'true');
  }, [isProtected]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch('/.netlify/functions/check-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: input }),
      });

      const data = await response.json();

      if (data.success) {
        setCookie(COOKIE_NAME, 'true', 7);
        setHasAccess(true);
      } else {
        setError('Incorrect password');
      }
    } catch (err) {
      setError('Server error. Please try again later.');
    }
  };

  if (!hasAccess) {
    return (
      <div
        className="min-h-screen bg-cover bg-center flex items-center justify-center px-4"
        style={{ backgroundImage: 'url(/images/background-password-page.png)' }}
      >
        <div className="p-12 w-full max-w-2xl text-center">
          <div className="mx-auto mb-10 w-28 h-28">
            <img
              src="/images/Icon - Lock.svg"
              alt="Lock icon"
              className="w-full h-full"
            />
          </div>

          <h1 className="text-5xl font-extrabold font-poppins-semi-bold text-white mb-4 tracking-tight">
            THIS CONTENT IS PROTECTED.
          </h1>

          <p className="text-white font-poppins-medium text-xl mb-8">
            To view, please enter the password.
          </p>

          <div className="bg-white rounded-2xl shadow-2xl p-8">

            <form onSubmit={handleSubmit} className="space-y-6 text-left">
              <p className="text-2xl m-0 font-poppins-medium text-black">Enter Password</p>
              <TextField
                id="password"
                type="password"
                variant="outlined"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                fullWidth
                size="medium"
                inputProps={{
                  style: {
                    fontSize: '1.4rem',
                    padding: '0.8rem',
                    borderRadius: '12px',
                  },
                }}
                error={Boolean(error)}
                helperText={error}
                FormHelperTextProps={{
                  style: { fontSize: '1rem' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />

              <button
                type="submit"
                className="w-1/3 py-2 bg-impblue500 text-white text-lg font-semibold rounded-lg hover:bg-blue-900 transition"
              >
                SUBMIT
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default PasswordGate;
